import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableRow,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const FurnitureDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [furniture, setFurniture] = useState(null);
  const [loading, setLoading] = useState(true);

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this furniture item?')) {
      try {
        await axios.delete(`${API_URL}/furniture/${id}`);
        navigate('/furniture');
      } catch (error) {
        console.error('Error deleting furniture item:', error);
        alert('Failed to delete furniture item');
      }
    }
  };

  const handleEdit = () => {
    navigate(`/furniture?edit=${id}`);
  };

  useEffect(() => {
    fetchFurniture();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchFurniture = async () => {
    try {
      const response = await axios.get(`${API_URL}/furniture/${id}`);
      setFurniture(response.data.furniture);
    } catch (error) {
      console.error('Error fetching furniture:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!furniture) {
    return (
      <Container sx={{ mt: 4 }}>
        <Typography>Furniture item not found</Typography>
      </Container>
    );
  }

  const getActionColor = (action) => {
    const colors = {
      keep: 'success',
      store: 'info',
      sell: 'warning',
      discard: 'error',
    };
    return colors[action] || 'default';
  };

  const recommendation = furniture.recommendation || {};

  return (
    <Container sx={{ mt: 4, mb: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/furniture')}>
          Back to Furniture
        </Button>
        <Box>
          <Button variant="outlined" startIcon={<EditIcon />} onClick={handleEdit} sx={{ mr: 1 }}>
            Edit
          </Button>
          <Button variant="outlined" color="error" startIcon={<DeleteIcon />} onClick={handleDelete}>
            Delete
          </Button>
        </Box>
      </Box>

      <Typography variant="h4" component="h1" gutterBottom>
        {furniture.name}
      </Typography>

      <Box mb={2}>
        <Chip label={furniture.category} sx={{ mr: 1 }} />
        <Chip label={furniture.condition} sx={{ mr: 1 }} />
        {furniture.action && (
          <Chip
            label={furniture.action}
            color={getActionColor(furniture.action)}
          />
        )}
      </Box>

      <Grid container spacing={3} sx={{ mt: 1 }}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Basic Information
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Table size="small">
                <TableBody>
                  {furniture.description && (
                    <TableRow>
                      <TableCell><strong>Description</strong></TableCell>
                      <TableCell>{furniture.description}</TableCell>
                    </TableRow>
                  )}
                  {furniture.seats && (
                    <TableRow>
                      <TableCell><strong>Number of Seats</strong></TableCell>
                      <TableCell>{furniture.seats}</TableCell>
                    </TableRow>
                  )}
                  {furniture.cushion_type && (
                    <TableRow>
                      <TableCell><strong>Cushion Type</strong></TableCell>
                      <TableCell>{furniture.cushion_type}</TableCell>
                    </TableRow>
                  )}
                  {furniture.material && (
                    <TableRow>
                      <TableCell><strong>Material</strong></TableCell>
                      <TableCell>{furniture.material}</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Dimensions & Volume
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Table size="small">
                <TableBody>
                  {furniture.length && (
                    <TableRow>
                      <TableCell><strong>Length (ft)</strong></TableCell>
                      <TableCell>{furniture.length}</TableCell>
                    </TableRow>
                  )}
                  {furniture.width && (
                    <TableRow>
                      <TableCell><strong>Width (ft)</strong></TableCell>
                      <TableCell>{furniture.width}</TableCell>
                    </TableRow>
                  )}
                  {furniture.height && (
                    <TableRow>
                      <TableCell><strong>Height (ft)</strong></TableCell>
                      <TableCell>{furniture.height}</TableCell>
                    </TableRow>
                  )}
                  {furniture.volume && (
                    <TableRow>
                      <TableCell><strong>Volume (cu ft)</strong></TableCell>
                      <TableCell>{parseFloat(furniture.volume).toFixed(2)}</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Financial Information
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Table size="small">
                <TableBody>
                  {furniture.estimated_value && (
                    <TableRow>
                      <TableCell><strong>Estimated Value</strong></TableCell>
                      <TableCell>₹{furniture.estimated_value}</TableCell>
                    </TableRow>
                  )}
                  {furniture.sale_price && (
                    <TableRow>
                      <TableCell><strong>Potential Sale Price</strong></TableCell>
                      <TableCell>₹{furniture.sale_price}</TableCell>
                    </TableRow>
                  )}
                  {furniture.rental_cost && (
                    <TableRow>
                      <TableCell><strong>Rental Cost</strong></TableCell>
                      <TableCell>₹{furniture.rental_cost}</TableCell>
                    </TableRow>
                  )}
                  {furniture.new_cost && (
                    <TableRow>
                      <TableCell><strong>Cost of New Item</strong></TableCell>
                      <TableCell>₹{furniture.new_cost}</TableCell>
                    </TableRow>
                  )}
                  {furniture.refurnishment_cost && (
                    <TableRow>
                      <TableCell><strong>Refurnishment Cost</strong></TableCell>
                      <TableCell>₹{furniture.refurnishment_cost}</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Recommendations
              </Typography>
              <Divider sx={{ mb: 2 }} />
              {recommendation.keep && recommendation.keep.length > 0 && (
                <Box mb={2}>
                  <Typography variant="subtitle2" color="success.main">Keep:</Typography>
                  {recommendation.keep.map((rec, idx) => (
                    <Typography key={idx} variant="body2">• {rec}</Typography>
                  ))}
                </Box>
              )}
              {recommendation.store && recommendation.store.length > 0 && (
                <Box mb={2}>
                  <Typography variant="subtitle2" color="info.main">Store:</Typography>
                  {recommendation.store.map((rec, idx) => (
                    <Typography key={idx} variant="body2">• {rec}</Typography>
                  ))}
                </Box>
              )}
              {recommendation.sell && recommendation.sell.length > 0 && (
                <Box mb={2}>
                  <Typography variant="subtitle2" color="warning.main">Sell:</Typography>
                  {recommendation.sell.map((rec, idx) => (
                    <Typography key={idx} variant="body2">• {rec}</Typography>
                  ))}
                </Box>
              )}
              {recommendation.discard && recommendation.discard.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" color="error.main">Discard:</Typography>
                  {recommendation.discard.map((rec, idx) => (
                    <Typography key={idx} variant="body2">• {rec}</Typography>
                  ))}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
};

export default FurnitureDetail;

